import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, Empty, Form, Input, Select, Button, message } from "antd";
import {
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  LoadingOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  handleExpiredTokenError,
} from "../../../../utils/ErrorUtils";
import fallbackGameIcon from "../../../../assets/icons/ic_casino.svg";
import EditCasinoGameModal, {
  EDIT_GAME_ACCEPTED_TYPES,
  EDIT_GAME_MAX_BYTES,
  type EditCasinoGameFormValues,
} from "./pages/EditCasinoGameModal";
import TagModal from "./pages/TagModal";
import CasinoGamesSchedulerMenu from "./CasinoGamesSchedulerMenu";
import {
  type CasinoConfigGameRow,
  type GamesGroupSortMode,
  type GameGroupMeta,
  formatGameIdForDisplay,
  formatGameIdForModalSubtitle,
  gameIconUrl,
  getCasinoConfigGameKey,
} from "../../../../utils/game-utils/gamesUtils";
import { CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY } from "../../../../queries/casino-configuration/games/casinoConfigurationGamesQueryKeys";
import {
  type CasinoGamesTreeGroupApi,
  casinoGamesTreeGroupKey,
} from "../../../../queries/casino-configuration/games/fetchCasinoGamesTree";
import { useCasinoGamesTree } from "../../../../queries/casino-configuration/games/useCasinoGamesTree";
import { useCasinoConfigurationBulkToggleGames } from "../../../../queries/casino-configuration/games/useCasinoConfigurationBulkToggleGames";
import {
  useCasinoConfigurationBulkActiveGamesByIds,
  CASINO_BULK_ACTIVE_BY_IDS_MAX,
  type CasinoBulkGameIdentity,
} from "../../../../queries/casino-configuration/games/useCasinoConfigurationBulkActiveGamesByIds";
import { usePatchCasinoConfigurationGameDetails } from "../../../../queries/casino-configuration/games/usePatchCasinoConfigurationGameDetails";

import { useCasinoGamesTreeGroup } from "../../../../queries/casino-configuration/games/useCasinoGamesTreeGroup";
import { fetchAllCasinoGamesTreeGroupPages } from "../../../../queries/casino-configuration/games/fetchCasinoGamesTreeGroupPage";
import { useAdminTags } from "../../../../queries/casino-configuration/games/useAdminTags";
import { useSetGameTags } from "../../../../queries/casino-configuration/games/useSetGameTags";
import {
  uploadCasinoImageToS3,
  buildS3UrlFromPresigned,
} from "../../../../utils/casinoImageUpload";
import { useCanUploadCasinoFmV3Assets } from "../../../../utils/casinoFmPermissions";

import { API_ROUTES } from "../../../../api/ApiRoutes";
import { getAuthedInstance } from "../../../../queries/casino-configuration/collections/useCollectionGames";
import {
  buildCasinoGamesCsv,
  buildCasinoGamesCsvFilename,
  downloadCasinoGamesCsv,
} from "./casinoGamesCsv";

import { useCurationDisplayName } from "../../../../hooks/useCurationDisplayName";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import "./stylesGamesConfiguration.css";

const TREE_SEARCH_DEBOUNCE_MS = 300;

function sortTreeGroups(
  groups: CasinoGamesTreeGroupApi[],
  mode: GamesGroupSortMode,
): CasinoGamesTreeGroupApi[] {
  return [...groups].sort((a, b) => {
    if (mode === "provider") {
      const c = String(a.provider).localeCompare(String(b.provider));
      return c !== 0 ? c : String(a.sub).localeCompare(String(b.sub));
    }
    const c = String(a.sub).localeCompare(String(b.sub));
    return c !== 0 ? c : String(a.provider).localeCompare(String(b.provider));
  });
}

function treeGroupToMeta(
  group: CasinoGamesTreeGroupApi,
  mode: GamesGroupSortMode,
): GameGroupMeta {
  const provider = String(group.provider ?? "");
  const sub = String(group.sub ?? "");
  const activeCount = Number(group.active ?? 0);
  const total = Number(group.total ?? 0);
  const matchedRaw = group.matched_total;
  const matchedTotal =
    matchedRaw != null && matchedRaw !== undefined ? Number(matchedRaw) : null;
  const displayTotal =
    matchedTotal != null && !Number.isNaN(matchedTotal) && matchedTotal >= 0
      ? matchedTotal
      : total;
  let status: GameGroupMeta["status"] = "mixed";
  if (activeCount === 0) status = "off";
  else if (total > 0 && activeCount === total) status = "all_on";

  return {
    key: casinoGamesTreeGroupKey(provider, sub),
    title:
      mode === "provider" ? `${provider} / ${sub}` : `${sub} / ${provider}`,
    provider,
    sub,
    games: [],
    activeCount,
    total,
    matchedTotal,
    displayTotal,
    status,
  };
}

function parseGroupKey(key: string | null): {
  provider: string | null;
  sub: string | null;
} {
  if (!key) return { provider: null, sub: null };
  const idx = key.indexOf("::");
  if (idx < 0) return { provider: key, sub: null };
  return { provider: key.slice(0, idx), sub: key.slice(idx + 2) };
}

function getDisplayName(game: CasinoConfigGameRow): string {
  return String(game.name || game.game_name || "").trim();
}

function CasinoGamesGroupTitle({ title }: { title: string }) {
  const parts = title
    .split(" / ")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return <span className="casino-games-group-title-text">{title}</span>;
  }
  const primary = parts[0];
  const secondary = parts.slice(1).join(" / ");
  return (
    <>
      <span className="casino-games-group-title-primary">{primary}</span>
      <span className="casino-games-group-title-sep"> / </span>
      <span className="casino-games-group-title-secondary">{secondary}</span>
    </>
  );
}

function GroupMasterToggle({
  group,
  disabled,
  toggling,
  onMasterChange,
}: {
  group: GameGroupMeta;
  disabled: boolean;
  toggling: boolean;
  onMasterChange: (next: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { activeCount, displayTotal } = group;

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.indeterminate = activeCount > 0 && activeCount < displayTotal;
  }, [activeCount, displayTotal]);

  const checked = displayTotal > 0 && activeCount === displayTotal;

  return (
    <label
      className="casino-games-toggle-switch"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled || toggling}
        onChange={(e) => onMasterChange(e.target.checked)}
        aria-label={AppStrings.CASINO_CONFIG_GAMES_FORM_ACTIVE_LABEL}
      />
      <span className="casino-games-toggle-slider" />
    </label>
  );
}

function GamesConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_GAMES_PAGE_TITLE);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setActiveSidebarTab } = useContext(AppContext);
  const canUploadCasinoFmV3Assets = useCanUploadCasinoFmV3Assets();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const {
    data: treeData,
    isPending: isTreePending,
    isFetching: isFetchingTree,
    isError: isTreeError,
    error: treeError,
    refetch: refetchTree,
  } = useCasinoGamesTree(debouncedSearch);
  const { mutateAsync: bulkToggleGames } =
    useCasinoConfigurationBulkToggleGames();
  const { mutateAsync: bulkActiveByIds } =
    useCasinoConfigurationBulkActiveGamesByIds();
  const { mutateAsync: patchGameDetails } =
    usePatchCasinoConfigurationGameDetails();

  const [sortMode, setSortMode] = useState<GamesGroupSortMode>("provider");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportEmptyMessage, setExportEmptyMessage] = useState<string | null>(
    null,
  );
  const prevGroupsSignatureRef = useRef<string | null>(null);
  const [togglingGameKeys, setTogglingGameKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [togglingGroupKeys, setTogglingGroupKeys] = useState<Set<string>>(
    () => new Set(),
  );
  // Selection persists across group expand/collapse so Operations can build a
  // cross-provider batch. Keyed by the row key; value carries the identity the
  // bulk-by-ids endpoint matches on.
  const [selectedGames, setSelectedGames] = useState<
    Map<string, CasinoBulkGameIdentity>
  >(() => new Map());
  const [bulkSubmitting, setBulkSubmitting] = useState<
    "enable" | "disable" | null
  >(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingGame, setEditingGame] = useState<CasinoConfigGameRow | null>(
    null,
  );
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [iconCleared, setIconCleared] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef<Promise<string> | null>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagModalGame, setTagModalGame] = useState<CasinoConfigGameRow | null>(
    null,
  );
  const { data: adminTags = [] } = useAdminTags();
  const { getName: getTagDisplayName } = useCurationDisplayName();
  const { mutateAsync: setGameTagsMutation, isPending: settingTags } =
    useSetGameTags();

  const [form] = Form.useForm<EditCasinoGameFormValues>();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, TREE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    setExportEmptyMessage(null);
  }, [searchQuery, sortMode]);

  const treeGroups = useMemo(
    () =>
      Array.isArray(treeData?.groups)
        ? (treeData.groups as CasinoGamesTreeGroupApi[])
        : [],
    [treeData],
  );

  const groups = useMemo(
    () =>
      sortTreeGroups(treeGroups, sortMode).map((group) =>
        treeGroupToMeta(group, sortMode),
      ),
    [treeGroups, sortMode],
  );

  const expandedKey =
    expandedKeys.size === 1 ? Array.from(expandedKeys)[0] : null;
  const { provider: expandedProvider, sub: expandedSub } =
    parseGroupKey(expandedKey);
  const groupPagesQuery = useCasinoGamesTreeGroup(
    expandedProvider,
    expandedSub,
    Boolean(expandedKey),
    debouncedSearch,
  );
  const expandedGames = useMemo(
    () => groupPagesQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [groupPagesQuery.data],
  );

  // Export only the open accordion group (one provider/sub). Fetching every
  // tree group on click was flooding the network and timing out large catalogs.
  const handleDownloadCsv = useCallback(async () => {
    if (exportingCsv) return;
    setExportEmptyMessage(null);

    if (groups.length === 0) {
      setExportEmptyMessage(AppStrings.CASINO_CONFIG_GAMES_EXPORT_EMPTY);
      return;
    }

    if (!expandedProvider || !expandedSub) {
      message.warning(AppStrings.CASINO_CONFIG_GAMES_EXPORT_EXPAND);
      return;
    }

    setExportingCsv(true);
    try {
      // Yield so the Ant button can paint its loading state before the await.
      await Promise.resolve();

      const rows = await fetchAllCasinoGamesTreeGroupPages(
        expandedProvider,
        expandedSub,
        debouncedSearch,
      );

      if (rows.length === 0) {
        setExportEmptyMessage(AppStrings.CASINO_CONFIG_GAMES_EXPORT_EMPTY);
        return;
      }

      const csv = buildCasinoGamesCsv(rows);
      downloadCasinoGamesCsv(csv, buildCasinoGamesCsvFilename(debouncedSearch));
    } catch {
      message.error(AppStrings.CASINO_CONFIG_GAMES_EXPORT_FAILED);
    } finally {
      setExportingCsv(false);
    }
  }, [
    exportingCsv,
    groups.length,
    expandedProvider,
    expandedSub,
    debouncedSearch,
  ]);

  const groupsSignature = useMemo(
    () => groups.map((g) => g.key).join("|"),
    [groups],
  );

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (!isTreeError || !treeError) return;
    message.error(AppStrings.FAILED_TO_FETCH_GAMES);
    handleExpiredTokenError(
      navigate,
      (treeError as { response?: unknown })?.response,
    );
  }, [treeError, isTreeError, navigate]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  useEffect(() => {
    setExpandedKeys((prev) => {
      const available = new Set(groups.map((group) => group.key));
      const next = new Set(
        Array.from(prev).filter((key) => available.has(key)),
      );

      if (next.size > 0) {
        prevGroupsSignatureRef.current = groupsSignature;
        return next;
      }
      if (groups.length === 0) {
        prevGroupsSignatureRef.current = groupsSignature;
        return new Set();
      }
      prevGroupsSignatureRef.current = groupsSignature;
      return new Set([groups[0].key]);
    });
  }, [groups, groupsSignature]);

  const runBulk = useCallback(
    async (targets: CasinoConfigGameRow[], is_active: boolean) => {
      const gameKeys = targets.map((game) => getCasinoConfigGameKey(game));
      setTogglingGameKeys((prev) => {
        const next = new Set(prev);
        gameKeys.forEach((key) => next.add(key));
        return next;
      });

      try {
        await bulkToggleGames({
          is_active,
          scope: {
            provider: String(targets[0].provider ?? ""),
            game_id: String(targets[0].game_id ?? targets[0].id ?? ""),
          },
        });
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        message.error(
          getApiErrorMessage(
            err,
            err?.message || AppStrings.FAILED_TO_UPDATE_GAME_STATUS,
          ),
        );
        handleExpiredTokenError(navigate, err?.response);
      } finally {
        setTogglingGameKeys((prev) => {
          const next = new Set(prev);
          gameKeys.forEach((key) => next.delete(key));
          return next;
        });
        await queryClient.invalidateQueries({
          queryKey: CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY,
        });
      }
    },
    [bulkToggleGames, navigate, queryClient],
  );

  const onGroupMasterChange = (group: GameGroupMeta, wantActive: boolean) => {
    const provider = String(group.provider ?? "");
    const sub = String(group.sub ?? "");
    if (!provider || !sub) return;

    setTogglingGroupKeys((prev) => new Set(prev).add(group.key));
    void (async () => {
      try {
        await bulkToggleGames({
          is_active: wantActive,
          scope: { provider, sub },
        });
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        message.error(
          getApiErrorMessage(
            err,
            err?.message || AppStrings.FAILED_TO_UPDATE_GAME_STATUS,
          ),
        );
        handleExpiredTokenError(navigate, err?.response);
      } finally {
        setTogglingGroupKeys((prev) => {
          const next = new Set(prev);
          next.delete(group.key);
          return next;
        });
        await queryClient.invalidateQueries({
          queryKey: CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY,
        });
      }
    })();
  };

  const onGameToggle = (game: CasinoConfigGameRow, wantActive: boolean) => {
    if (wantActive === (game.is_active !== false)) return;
    void runBulk([game], wantActive);
  };

  const gameIdentity = (game: CasinoConfigGameRow): CasinoBulkGameIdentity => ({
    provider: String(game.provider ?? ""),
    game_id: String(game.game_id ?? game.id ?? ""),
  });

  const toggleGameSelection = (game: CasinoConfigGameRow) => {
    const gkey = getCasinoConfigGameKey(game);
    setSelectedGames((prev) => {
      const next = new Map(prev);
      if (next.has(gkey)) next.delete(gkey);
      else next.set(gkey, gameIdentity(game));
      return next;
    });
  };

  const toggleSelectAllVisible = (
    visible: CasinoConfigGameRow[],
    selectAll: boolean,
  ) => {
    setSelectedGames((prev) => {
      const next = new Map(prev);
      for (const game of visible) {
        const gkey = getCasinoConfigGameKey(game);
        if (selectAll) next.set(gkey, gameIdentity(game));
        else next.delete(gkey);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedGames(new Map());

  const submitBulkActive = (is_active: boolean) => {
    const games = Array.from(selectedGames.values());
    if (games.length === 0) return;
    if (games.length > CASINO_BULK_ACTIVE_BY_IDS_MAX) {
      message.error(
        AppStrings.CASINO_CONFIG_GAMES_BULK_LIMIT.replace(
          "{max}",
          String(CASINO_BULK_ACTIVE_BY_IDS_MAX),
        ),
      );
      return;
    }

    setBulkSubmitting(is_active ? "enable" : "disable");
    void (async () => {
      try {
        const result = await bulkActiveByIds({ is_active, games });
        const template = is_active
          ? AppStrings.CASINO_CONFIG_GAMES_BULK_ENABLE_SUCCESS
          : AppStrings.CASINO_CONFIG_GAMES_BULK_DISABLE_SUCCESS;
        if (result.affected === 0) {
          message.info(AppStrings.CASINO_CONFIG_GAMES_BULK_NO_CHANGE);
        } else {
          message.success(
            template
              .replace("{affected}", String(result.affected))
              .replace("{requested}", String(result.requested)),
          );
        }
        clearSelection();
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        message.error(
          getApiErrorMessage(
            err,
            err?.message || AppStrings.CASINO_CONFIG_GAMES_BULK_ERROR,
          ),
        );
        handleExpiredTokenError(navigate, err?.response);
      } finally {
        // The mutation hook already invalidates the games tree in its
        // onSettled, so no manual invalidation is needed here.
        setBulkSubmitting(null);
      }
    })();
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.clear();
        next.add(key);
      }
      return next;
    });
  };

  const openEdit = (game: CasinoConfigGameRow) => {
    setEditingGame(game);
    const name = getDisplayName(game) || formatGameIdForDisplay(game);
    form.setFieldsValue({ name });
    setIconCleared(false);
    setSelectedFile(null);
    uploadRef.current = null;
    setUploading(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingGame(null);
    form.resetFields();
    setSelectedFile(null);
    setIconCleared(false);
    uploadRef.current = null;
    setUploading(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
  };

  const beforeUpload = (file: File) => {
    if (!canUploadCasinoFmV3Assets) {
      message.error(AppStrings.PAM_UPLOADS_PERMISSION_REQUIRED);
      return false;
    }

    const mimeOk = EDIT_GAME_ACCEPTED_TYPES.includes(file.type);
    const extOk = /\.png$|\.webp$/i.test(file.name || "");
    if (!mimeOk && !extOk) {
      message.error(AppStrings.CASINO_CONFIG_GAMES_UPLOAD_TYPE_ERROR);
      return false;
    }
    if (file.size > EDIT_GAME_MAX_BYTES) {
      message.error(AppStrings.CASINO_CONFIG_GAMES_UPLOAD_SIZE_ERROR);
      return false;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setIconCleared(false);
    setPreviewUrl(URL.createObjectURL(file));

    if (editingGame) {
      const provider = String(editingGame.provider ?? "");
      const rawGameId = String(editingGame.game_id ?? editingGame.id ?? "");
      const customImage = (editingGame as Record<string, unknown>)
        .custom_image as string | undefined;

      setUploading(true);
      const upload = uploadCasinoImageToS3({
        imageType: "game_custom_image",
        identifier: `${provider}_${rawGameId}`,
        contentType: file.type,
        file,
        ...(customImage ? { existingUrl: customImage } : {}),
      }).then((presigned) => {
        const url = buildS3UrlFromPresigned(presigned);
        if (!url)
          throw new Error(AppStrings.CASINO_CONFIG_GAMES_IMAGE_UPLOAD_FAILED);

        return url;
      });
      uploadRef.current = upload;
      upload
        .catch((err: unknown) => {
          const e = err as { message?: string };
          message.error(
            e?.message || AppStrings.CASINO_CONFIG_GAMES_IMAGE_UPLOAD_FAILED,
          );
          setSelectedFile(null);
        })
        .finally(() => {
          if (uploadRef.current === upload) setUploading(false);
        });
    }

    return false;
  };

  const removeIcon = () => {
    setSelectedFile(null);
    setIconCleared(true);
    uploadRef.current = null;
    setUploading(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
  };

  const saveEdit = async () => {
    if (!editingGame) return;

    try {
      const values = await form.validateFields();

      setSavingEdit(true);

      let uploadedImageUrl: string | null = null;
      const pendingUpload = uploadRef.current;
      if (pendingUpload) {
        try {
          uploadedImageUrl = await pendingUpload;
        } catch {
          message.error(AppStrings.CASINO_CONFIG_GAMES_IMAGE_UPLOAD_INCOMPLETE);
          return;
        }
      }

      const provider = String(editingGame.provider ?? "");
      const rawGameId = String(editingGame.game_id ?? editingGame.id ?? "");
      const prevName = getDisplayName(editingGame);
      const nextName = values.name.trim();
      const nameChanged = nextName !== prevName;

      const needsDetailsPatch = nameChanged || (iconCleared && !selectedFile);

      if (needsDetailsPatch || uploadedImageUrl) {
        await patchGameDetails({
          provider,
          game_id: rawGameId,
          ...(nameChanged ? { display_name: nextName } : {}),
          ...(iconCleared && !selectedFile ? { clear_thumbnail: true } : {}),
          ...(uploadedImageUrl ? { custom_image: uploadedImageUrl } : {}),
        });
      }

      message.success(AppStrings.CASINO_CONFIG_GAMES_SAVE_SUCCESS);
      closeEdit();
    } catch (error: unknown) {
      if ((error as { errorFields?: unknown[] })?.errorFields) return;
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(
        getApiErrorMessage(
          err,
          err?.message || AppStrings.CASINO_CONFIG_GAMES_SAVE_ERROR,
        ),
      );
      handleExpiredTokenError(navigate, err?.response);
    } finally {
      setSavingEdit(false);
    }
  };

  const openTagModal = (game: CasinoConfigGameRow) => {
    setTagModalGame(game);
    setTagModalOpen(true);
  };

  const closeTagModal = () => {
    setTagModalOpen(false);
    setTagModalGame(null);
  };

  const handleSetTag = async (tagIds: string[]) => {
    if (!tagModalGame) return;
    try {
      await setGameTagsMutation({
        provider: String(tagModalGame.provider ?? ""),
        game_id: String(tagModalGame.game_id ?? tagModalGame.id ?? ""),
        tag_ids: tagIds,
      });
      setTagModalGame((prev) => (prev ? { ...prev, tags: tagIds } : null));
      void queryClient.invalidateQueries({
        queryKey: CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY,
      });
      message.success(AppStrings.CASINO_CONFIG_GAMES_TAG_SET_SUCCESS);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(
        getApiErrorMessage(
          err,
          err?.message || AppStrings.CASINO_CONFIG_GAMES_TAG_SET_ERROR,
        ),
      );
      throw error;
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!tagModalGame) return;
    const provider = String(tagModalGame.provider ?? "");
    const gameId = String(tagModalGame.game_id ?? tagModalGame.id ?? "");
    try {
      const instance = getAuthedInstance();
      const url = API_ROUTES.DELETE_GAME_TAG.replace(
        "{provider}",
        encodeURIComponent(provider),
      )
        .replace("{game_id}", encodeURIComponent(gameId))
        .replace("{tag_id}", encodeURIComponent(tagId));
      await instance.delete(url);
      setTagModalGame((prev) =>
        prev
          ? { ...prev, tags: (prev.tags ?? []).filter((t) => t !== tagId) }
          : null,
      );
      void queryClient.invalidateQueries({
        queryKey: CASINO_CONFIGURATION_GAMES_SCOPE_QUERY_KEY,
      });
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(
        getApiErrorMessage(
          err,
          err?.message || AppStrings.CASINO_CONFIG_GAMES_TAG_SET_ERROR,
        ),
      );
      throw error;
    }
  };

  const statusLabel = (status: GameGroupMeta["status"]) => {
    if (status === "all_on") {
      return AppStrings.CASINO_CONFIG_GAMES_GROUP_STATUS_ALL_ON;
    }
    if (status === "off") {
      return AppStrings.CASINO_CONFIG_GAMES_GROUP_STATUS_OFF;
    }
    return AppStrings.CASINO_CONFIG_GAMES_GROUP_STATUS_MIXED;
  };

  const statusClass = (status: GameGroupMeta["status"]) => {
    if (status === "off") return "casino-games-group-status-off";
    return "casino-games-group-status-mixed";
  };

  const showInitialTreeLoader = isTreePending && treeData === undefined;

  if (showInitialTreeLoader) {
    return (
      <>
        <div className="casino-games-shell">
          <div className="casino-games-initial-loader">
            <RebetLoader height="100px" width="100px" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="casino-games-shell">
        <div className="casino-games-config">
          <div className="casino-games-toolbar">
            <Input
              className={`casino-games-search${isFetchingTree ? " casino-games-search--refreshing" : ""}`}
              prefix={
                isFetchingTree ? (
                  <LoadingOutlined className="casino-games-search-loading-icon" />
                ) : (
                  <SearchOutlined />
                )
              }
              placeholder={AppStrings.CASINO_CONFIG_GAMES_SEARCH_PLACEHOLDER}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onPressEnter={(e) => e.preventDefault()}
              allowClear
            />
            <div className="casino-games-toolbar-actions">
              <div className="casino-games-sort-wrap">
                <span>{AppStrings.CASINO_CONFIG_GAMES_SORT_BY_LABEL}</span>
                <Select<GamesGroupSortMode>
                  value={sortMode}
                  onChange={setSortMode}
                  options={[
                    {
                      value: "provider",
                      label: AppStrings.CASINO_CONFIG_GAMES_SORT_PROVIDER,
                    },
                    {
                      value: "subprovider",
                      label: AppStrings.CASINO_CONFIG_GAMES_SORT_SUBPROVIDER,
                    },
                  ]}
                />
              </div>
              <div className="casino-games-download-wrap">
                <Button
                  type="default"
                  className="casino-games-download-btn"
                  icon={<DownloadOutlined />}
                  loading={exportingCsv}
                  onClick={handleDownloadCsv}
                  aria-label={AppStrings.CASINO_CONFIG_GAMES_DOWNLOAD_CSV}
                  data-testid="casino-games-download-csv"
                />
                {exportEmptyMessage ? (
                  <span
                    className="casino-games-export-empty"
                    data-testid="casino-games-export-empty"
                  >
                    {exportEmptyMessage}
                  </span>
                ) : null}
              </div>
              {/*
                CASENG-1058: operators re-run the catalog sync from here when
                games are missing. A successful run changes the counts on
                screen, so refresh the tree.
              */}
              <CasinoGamesSchedulerMenu
                onRunFinished={() => {
                  refetchTree();
                }}
              />
            </div>
          </div>

          <div className="casino-games-list">
            {groups.length === 0 ? (
              <div className="casino-games-empty">
                <Empty description={AppStrings.CASINO_CONFIG_GAMES_EMPTY} />
              </div>
            ) : (
              groups.map((group) => {
                const expanded = expandedKeys.has(group.key);
                const groupToggling = togglingGroupKeys.has(group.key);
                const isExpandedGroup = expandedKey === group.key;
                const visibleGames = isExpandedGroup ? expandedGames : [];
                const selectedVisibleCount = visibleGames.filter((g) =>
                  selectedGames.has(getCasinoConfigGameKey(g)),
                ).length;
                const allVisibleSelected =
                  visibleGames.length > 0 &&
                  selectedVisibleCount === visibleGames.length;
                const someVisibleSelected =
                  selectedVisibleCount > 0 && !allVisibleSelected;

                return (
                  <div className="casino-games-group" key={group.key}>
                    <div
                      className="casino-games-group-header"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleExpand(group.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleExpand(group.key);
                        }
                      }}
                    >
                      <span className="casino-games-group-chevron">
                        {expanded ? <DownOutlined /> : <RightOutlined />}
                      </span>
                      <span className="casino-games-group-title">
                        <CasinoGamesGroupTitle title={group.title} />
                      </span>
                      <span className="casino-games-group-badge">
                        {group.activeCount}/{group.displayTotal}
                      </span>
                      <span
                        className={`casino-games-group-status ${statusClass(group.status)}`}
                      >
                        {statusLabel(group.status)}
                      </span>
                      <GroupMasterToggle
                        group={group}
                        disabled={false}
                        toggling={groupToggling}
                        onMasterChange={(next) =>
                          onGroupMasterChange(group, next)
                        }
                      />
                    </div>

                    {expanded ? (
                      <div className="casino-games-group-body">
                        {isExpandedGroup && groupPagesQuery.isLoading ? (
                          <div className="casino-games-group-loader">
                            <RebetLoader height="56px" width="56px" />
                          </div>
                        ) : null}
                        <div className="casino-games-table-head">
                          <span className="casino-games-select-cell">
                            <Checkbox
                              checked={allVisibleSelected}
                              indeterminate={someVisibleSelected}
                              disabled={
                                visibleGames.length === 0 ||
                                bulkSubmitting !== null
                              }
                              onChange={(e) =>
                                toggleSelectAllVisible(
                                  visibleGames,
                                  e.target.checked,
                                )
                              }
                              aria-label={
                                AppStrings.CASINO_CONFIG_GAMES_SELECT_ALL_ARIA
                              }
                            />
                          </span>
                          <span>
                            {AppStrings.CASINO_CONFIG_GAMES_COL_THUMB}
                          </span>
                          <span>{AppStrings.CASINO_CONFIG_GAMES_COL_NAME}</span>
                          <span>
                            {AppStrings.CASINO_CONFIG_GAMES_COL_GAME_ID}
                          </span>
                          <span>
                            {AppStrings.CASINO_CONFIG_GAMES_COL_FLAGS}
                          </span>
                          <span>{AppStrings.CASINO_CONFIG_GAMES_COL_TAGS}</span>
                          <span>
                            {AppStrings.CASINO_CONFIG_GAMES_COL_ACTIVE}
                          </span>
                        </div>
                        {visibleGames.map((game) => {
                          const gkey = getCasinoConfigGameKey(game);
                          const busy = togglingGameKeys.has(gkey);
                          const active = game.is_active !== false;
                          const thumb = gameIconUrl(game);
                          const gameName =
                            getDisplayName(game) || AppStrings.UNKNOWN_GAME;
                          const isAggregator = Boolean(game.aggregator);
                          const gameTags = Array.isArray(game.tags)
                            ? game.tags
                            : [];

                          return (
                            <div
                              className="casino-games-row"
                              key={gkey}
                              data-testid={`casino-config-game-row-${gkey}`}
                            >
                              <div className="casino-games-select-cell">
                                <Checkbox
                                  checked={selectedGames.has(gkey)}
                                  disabled={bulkSubmitting !== null}
                                  onChange={() => toggleGameSelection(game)}
                                  aria-label={AppStrings.CASINO_CONFIG_GAMES_SELECT_ROW_ARIA.replace(
                                    "{name}",
                                    gameName,
                                  )}
                                />
                              </div>
                              <div>
                                {thumb ? (
                                  <img
                                    className="casino-games-thumb"
                                    data-testid={`casino-config-game-thumb-${gkey}`}
                                    src={thumb}
                                    alt=""
                                  />
                                ) : (
                                  <div className="casino-games-thumb-placeholder">
                                    {gameName.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div className="casino-games-name">
                                {gameName}
                              </div>
                              <div
                                className="casino-games-game-id"
                                title={formatGameIdForDisplay(game)}
                              >
                                {formatGameIdForDisplay(game)}
                              </div>
                              <div className="casino-games-flags-cell">
                                {isAggregator ? (
                                  <span className="casino-games-flag-badge">
                                    {AppStrings.CASINO_CONFIG_GAMES_FLAG_AGG}
                                  </span>
                                ) : null}
                              </div>
                              <div
                                className="casino-games-tags-cell"
                                data-testid={`casino-config-game-tags-${gkey}`}
                              >
                                {gameTags.length > 0 ? (
                                  (() => {
                                    const firstTag = gameTags[0];
                                    const tagObj = adminTags.find(
                                      (t) =>
                                        t.id === firstTag ||
                                        t.name.toLowerCase() ===
                                          firstTag.toLowerCase(),
                                    );
                                    const tagColor = tagObj?.color || "#9ea2b8";
                                    const tagLabel = tagObj
                                      ? getTagDisplayName(tagObj)
                                      : firstTag;
                                    return (
                                      <button
                                        type="button"
                                        className="casino-games-row-tag-badge"
                                        style={{
                                          borderColor: tagColor,
                                          color: tagColor,
                                        }}
                                        onClick={() => openTagModal(game)}
                                        title={
                                          tagObj && tagLabel !== tagObj.name
                                            ? tagObj.name
                                            : undefined
                                        }
                                      >
                                        <span
                                          className="casino-games-row-tag-dot"
                                          style={{ background: tagColor }}
                                        />
                                        {tagLabel}
                                      </button>
                                    );
                                  })()
                                ) : (
                                  <button
                                    type="button"
                                    className="casino-games-add-tag-btn"
                                    onClick={() => openTagModal(game)}
                                  >
                                    {AppStrings.CASINO_CONFIG_GAMES_TAG_ADD}
                                  </button>
                                )}
                              </div>
                              <div className="casino-games-active-cell">
                                <span
                                  className={`casino-games-active-label ${
                                    active
                                      ? "casino-games-active-label-on"
                                      : "casino-games-active-label-off"
                                  }`}
                                >
                                  {active
                                    ? AppStrings.CASINO_CONFIG_GAMES_STATUS_ON
                                    : AppStrings.CASINO_CONFIG_GAMES_STATUS_OFF}
                                </span>
                                <label className="casino-games-toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={active}
                                    disabled={busy}
                                    onChange={(e) =>
                                      onGameToggle(game, e.target.checked)
                                    }
                                    aria-label={
                                      AppStrings.CASINO_CONFIG_GAMES_FORM_ACTIVE_LABEL
                                    }
                                  />
                                  <span className="casino-games-toggle-slider" />
                                </label>
                                <button
                                  type="button"
                                  className="casino-games-edit-btn"
                                  aria-label={AppStrings.CASINO_CONFIG_GAMES_EDIT_ARIA.replace(
                                    "{name}",
                                    gameName,
                                  )}
                                  onClick={() => openEdit(game)}
                                >
                                  <EditOutlined />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {isExpandedGroup && groupPagesQuery.hasNextPage ? (
                          <div className="casino-games-load-more">
                            <button
                              type="button"
                              className="casino-games-load-more-btn"
                              disabled={groupPagesQuery.isFetchingNextPage}
                              onClick={() =>
                                void groupPagesQuery.fetchNextPage()
                              }
                            >
                              {groupPagesQuery.isFetchingNextPage
                                ? AppStrings.CASINO_CONFIG_GAMES_LOADING
                                : AppStrings.CASINO_CONFIG_GAMES_LOAD_MORE}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {selectedGames.size > 0 ? (
        <div
          className="casino-games-bulk-bar"
          role="region"
          aria-label={AppStrings.CASINO_CONFIG_GAMES_BULK_BAR_ARIA}
        >
          <span className="casino-games-bulk-count">
            {AppStrings.CASINO_CONFIG_GAMES_BULK_SELECTED.replace(
              "{count}",
              String(selectedGames.size),
            )}
          </span>
          <div className="casino-games-bulk-actions">
            <button
              type="button"
              className="casino-games-bulk-clear"
              disabled={bulkSubmitting !== null}
              onClick={clearSelection}
            >
              {AppStrings.CASINO_CONFIG_GAMES_BULK_CLEAR}
            </button>
            <button
              type="button"
              className="casino-games-bulk-disable"
              disabled={bulkSubmitting !== null}
              onClick={() => submitBulkActive(false)}
            >
              {bulkSubmitting === "disable" ? (
                <LoadingOutlined />
              ) : (
                AppStrings.CASINO_CONFIG_GAMES_BULK_DISABLE
              )}
            </button>
            <button
              type="button"
              className="casino-games-bulk-enable"
              disabled={bulkSubmitting !== null}
              onClick={() => submitBulkActive(true)}
            >
              {bulkSubmitting === "enable" ? (
                <LoadingOutlined />
              ) : (
                AppStrings.CASINO_CONFIG_GAMES_BULK_ENABLE
              )}
            </button>
          </div>
        </div>
      ) : null}

      <EditCasinoGameModal
        open={editOpen}
        game={editingGame}
        onCancel={closeEdit}
        onSave={() => void saveEdit()}
        saving={savingEdit}
        uploading={uploading}
        form={form}
        previewUrl={previewUrl}
        currentIconUrl={editingGame ? gameIconUrl(editingGame) : ""}
        fallbackIcon={fallbackGameIcon}
        onBeforeUpload={beforeUpload}
        onRemoveIcon={removeIcon}
        canUploadImages={canUploadCasinoFmV3Assets}
        onManageTags={() => {
          if (editingGame) {
            closeEdit();
            openTagModal(editingGame);
          }
        }}
        adminTags={adminTags}
      />

      <TagModal
        open={tagModalOpen}
        gameLabel={
          tagModalGame ? formatGameIdForModalSubtitle(tagModalGame) : ""
        }
        currentTags={(tagModalGame?.tags ?? []).map((t: unknown) => {
          if (typeof t !== "string") {
            const obj = t as Record<string, unknown>;
            return String(obj?.tag_id ?? obj?.id ?? obj?.name ?? t);
          }
          const matchById = adminTags.find((at) => at.id === t);
          if (matchById) return matchById.id;
          const matchByName = adminTags.find(
            (at) => at.name.toLowerCase() === t.toLowerCase(),
          );
          if (matchByName) return matchByName.id;
          return t;
        })}
        availableTags={adminTags}
        loading={settingTags}
        onSetTag={handleSetTag}
        onRemoveTag={handleRemoveTag}
        onClose={closeTagModal}
      />
    </>
  );
}

export default GamesConfigurationPage;
